/**
 * Registry cross-tab de tabs vivas (BroadcastChannel).
 */

import { TAB_ALIVE_BROADCAST_MS, TAB_REGISTRY_STALE_MS } from './coordinationConstants';

const CHANNEL_NAME = 'gomarke_pixel_queue';

type RegistryMessage =
  | { type: 'tab_alive'; tabId: string; at: number }
  | { type: 'tab_dead'; tabId: string; at: number };

const aliveAt = new Map<string, number>();
let localTabId = '';
let registryChannel: BroadcastChannel | null = null;
let broadcastTimer: ReturnType<typeof setInterval> | null = null;

function pruneStale(now: number): void {
  for (const [id, at] of aliveAt) {
    if (now - at > TAB_REGISTRY_STALE_MS) aliveAt.delete(id);
  }
}

export function initTabRegistry(tabId: string): void {
  localTabId = tabId;
  aliveAt.set(tabId, Date.now());
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return;
  }
  if (registryChannel) return;
  registryChannel = new BroadcastChannel(CHANNEL_NAME);
  registryChannel.onmessage = (ev: MessageEvent<RegistryMessage>) => {
    const msg = ev.data;
    if (msg?.type === 'tab_alive' && msg.tabId) {
      aliveAt.set(msg.tabId, msg.at);
    }
    if (msg?.type === 'tab_dead' && msg.tabId) {
      aliveAt.delete(msg.tabId);
    }
  };
  const ping = (): void => {
    const at = Date.now();
    aliveAt.set(localTabId, at);
    registryChannel?.postMessage({
      type: 'tab_alive',
      tabId: localTabId,
      at,
    } satisfies RegistryMessage);
    pruneStale(at);
  };
  ping();
  broadcastTimer = setInterval(ping, TAB_ALIVE_BROADCAST_MS);
}

export function getAliveTabIds(): Set<string> {
  const now = Date.now();
  pruneStale(now);
  const ids = new Set(aliveAt.keys());
  if (localTabId) ids.add(localTabId);
  return ids;
}

export function broadcastTabDead(tabId: string): void {
  aliveAt.delete(tabId);
  registryChannel?.postMessage({
    type: 'tab_dead',
    tabId,
    at: Date.now(),
  } satisfies RegistryMessage);
}

export function destroyTabRegistry(): void {
  if (broadcastTimer) {
    clearInterval(broadcastTimer);
    broadcastTimer = null;
  }
  if (localTabId) broadcastTabDead(localTabId);
  registryChannel?.close();
  registryChannel = null;
}

export function _testResetTabRegistry(): void {
  aliveAt.clear();
  localTabId = '';
}
